import type { Me, RoleKey } from '@myrewards/contracts';
import type { RouteObject } from 'react-router';
import { opensPage } from '../app/access';
import type { SearchResult } from '../shell/types';

/**
 * How a route shows in Search & jump (APP-022). Routes declare it in `handle.search`, so a new
 * screen joins search when it merges and search never lists a page that doesn't exist.
 */
export interface PageSearch {
  label: string;
  /** One line of context ("Your awards, elections and letters"). */
  meta: string;
  /** Defaults to the route's own path. */
  href?: string;
  /** Who can open the page; everyone signed in when left out. */
  visible?: (me: Me) => boolean;
}

export interface SearchPage extends PageSearch {
  href: string;
}

/** A page for anyone holding one of these roles, and for System Admins, who see every page. */
export function forRoles(...roles: readonly RoleKey[]): (me: Me) => boolean {
  return (me) => opensPage(me, roles);
}

/**
 * A page about the person's own work (their drafts, the letters they hand out): only for the roles
 * that have any. A System Admin can open it, but it would only ever be empty for them (APP-134 §7).
 */
export function forOwnWork(...roles: readonly RoleKey[]): (me: Me) => boolean {
  return (me) => me.roles.some((r) => roles.includes(r.role));
}

function isPageList(search: PageSearch | readonly PageSearch[]): search is readonly PageSearch[] {
  return Array.isArray(search);
}

function searchOf(route: RouteObject): readonly PageSearch[] {
  const handle = route.handle as { search?: PageSearch | readonly PageSearch[] } | undefined;
  const search = handle?.search;
  if (search === undefined) return [];
  return isPageList(search) ? search : [search];
}

function joinPath(base: string, path: string | undefined): string {
  if (path === undefined) return base;
  if (path.startsWith('/')) return path;
  return `${base.replace(/\/$/, '')}/${path}`;
}

/** Every page the route tree declares for search, with its full path. */
export function collectSearchPages(
  routes: readonly RouteObject[],
  base = '/',
): readonly SearchPage[] {
  const pages: SearchPage[] = [];
  for (const route of routes) {
    const path = route.index === true ? base : joinPath(base, route.path);
    for (const entry of searchOf(route)) {
      const href = entry.href ?? path;
      // A page with an id in its address is reached from a result, never jumped to on its own.
      if (!/[:*]/.test(href)) pages.push({ ...entry, href: href === '' ? '/' : href });
    }
    if (route.children !== undefined) pages.push(...collectSearchPages(route.children, path));
  }
  return pages;
}

/** Folds case and accents, so "resume" finds "Résumé". */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase();
}

/** The pages this person can open, as palette results. */
export function visiblePages(me: Me, pages: readonly SearchPage[]): SearchResult[] {
  const seen = new Set<string>();
  return pages.flatMap((page) => {
    if (page.visible !== undefined && !page.visible(me)) return [];
    if (seen.has(page.href)) return [];
    seen.add(page.href);
    return [
      {
        id: `page:${page.href}`,
        kind: 'page',
        label: page.label,
        meta: page.meta,
        href: page.href,
      },
    ];
  });
}

/** Pages whose name or description holds every word typed. */
export function matchPages(
  pages: readonly SearchResult[],
  query: string,
  limit = 5,
): SearchResult[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return pages
    .filter((page) => {
      const text = fold(`${page.label} ${page.meta ?? ''}`);
      return words.every((word) => text.includes(word));
    })
    .slice(0, limit);
}
